import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { whatsappSupportUrl } from "@/lib/brand";
import { communityConfig } from "@/lib/apiClient";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";

// Onboarding's own Back/Continue footer sits bottom-right on narrow screens —
// the exact same corner this bubble anchors to. Confirmed by actually driving
// the signup->onboarding flow at a mobile viewport: the bubble (and the iOS
// install card, see InstallPrompt.jsx) physically covered the Continue
// button, the same way a real fingertip tap would be blocked. Onboarding is
// a short, mandatory wizard — better to just stay out of its way entirely
// than risk sitting on top of the one button that lets a user finish it.
const HIDDEN_ROUTES = ["/onboarding"];

// Dismissal is per-browser-session, same convention as usePWAInstall's
// install-banner dismissal: closing it hides it until the browser is
// actually closed and reopened, not just navigated away from.
const POPUP_DISMISS_KEY = "qf_wa_popup_dismissed_v1";
const POPUP_DELAY_MS = 1800;
const POPUP_PHONE_VISIBLE_MS = 5000;

function safeSessionGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode, etc.) — popup just won't persist dismissal.
  }
}

/**
 * Floating "chat with support" bubble — bottom-right on every page, always
 * available, deliberately separate from the WhatsApp Channel card in
 * Community & Live Events (a Channel is broadcast-only and can never open a
 * real chat). Tapping this opens a real WhatsApp chat with the Business
 * number, pre-filled with the keyword the Wati bot's flow triggers on — the
 * user still has to press send themselves, since WhatsApp/Meta never lets a
 * link auto-send.
 *
 * A small speech-bubble nudge ("Any questions? Chat with us") appears above
 * the button a couple seconds after load, mirroring the callout bubble on
 * common chat widgets (Wati/Tawk/Intercom). It can be closed independently
 * of the button itself; closing it just hides the nudge for the session.
 *
 * Positioned at bottom-5/6 so it sits just below InstallPrompt's iOS-only
 * "Add to Home Screen" card (bottom-24/28) rather than overlapping it on the
 * rare occasion both are visible at once.
 *
 * Admin-gated (app_config.whatsapp_support_enabled, off by default — see
 * core.py's _community_config_dict) so this can ship now and stay hidden
 * until the real Wati number/flow is finalized, without another deploy.
 * Reads the public, unauthenticated /community/config — same call the
 * Landing page already makes for signed-out visitors — since this button
 * renders globally regardless of login state.
 */
export default function WhatsAppSupportButton() {
  const { t, dir } = useI18n();
  const { pathname } = useLocation();
  const [popupOpen, setPopupOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    communityConfig()
      .then((c) => setEnabled(Boolean(c?.whatsapp_support_enabled)))
      .catch(() => { /* offline/backend down — stay hidden rather than risk pointing at an unfinished number */ });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (safeSessionGet(POPUP_DISMISS_KEY) === "1") return undefined;
    let closer;
    const timer = setTimeout(() => {
      setPopupOpen(true);
      // On a phone the nudge sits over the page's own buttons (Continue, Back...), so it goes away on its own.
      if (window.matchMedia?.("(max-width: 639px)").matches) closer = setTimeout(() => setPopupOpen(false), POPUP_PHONE_VISIBLE_MS);
    }, POPUP_DELAY_MS);
    return () => { clearTimeout(timer); clearTimeout(closer); };
  }, [enabled]);

  const dismissPopup = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPopupOpen(false);
    safeSessionSet(POPUP_DISMISS_KEY, "1");
  };

  if (!enabled || HIDDEN_ROUTES.includes(pathname)) return null;

  return (
    // dir="ltr" is deliberate here, independent of the page's own language:
    // this is fixed screen chrome (a corner-anchored widget), not reading
    // content, so it must not mirror for RTL. Without it, `items-end` —
    // align-items: flex-end on a flex-col — resolves against the *inline*
    // axis, which direction flips: it means "physical right" in English but
    // "physical left" once Urdu sets dir="rtl" on <html>. The wrapper's own
    // `right-5` stayed put either way (that's a physical CSS property), but
    // the popup/button stack inside it shifted to the opposite corner the
    // moment the popup (220px) was wider than the button (56px) and had
    // slack to redistribute — that's what put the icon at bottom-left in
    // Urdu. The popup's own Urdu text still gets dir={dir} below so its
    // paragraph alignment/reading order stays correct despite the ltr
    // override up here.
    <div dir="ltr" className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 flex flex-col items-end gap-3">
      {popupOpen && (
        <a
          href={whatsappSupportUrl()}
          target="_blank"
          rel="noopener noreferrer"
          dir={dir}
          data-testid="whatsapp-support-popup"
          className="relative max-w-[220px] rounded-2xl bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-snug px-4 py-3 pr-8 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.28)] ring-1 ring-black/5 animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          {t("support.whatsapp_popup_text")}
          <button
            type="button"
            onClick={dismissPopup}
            aria-label={t("support.whatsapp_popup_dismiss")}
            className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full grid place-items-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:bg-slate-700 transition"
          >
            ×
          </button>
          <span
            aria-hidden="true"
            className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white dark:bg-slate-800 rotate-45"
          />
        </a>
      )}
      <a
        href={whatsappSupportUrl()}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-support-button"
        title={t("support.whatsapp_button_title")}
        aria-label={t("support.whatsapp_button_title")}
        className="w-14 h-14 rounded-full bg-[#10B981] hover:bg-[#0D9668] text-white grid place-items-center shadow-[0_12px_32px_-8px_rgba(16,185,129,0.55)] hover:scale-105 active:scale-95 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] focus-visible:ring-offset-2"
      >
        <WhatsAppIcon className="w-7 h-7" />
      </a>
    </div>
  );
}
